/* eslint-disable no-nested-ternary */
import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { useSpring, animated } from "@react-spring/web";
import { STORAGE_AUTH_TOKEN } from "constants/variables";

// Components
import GoTop from "components/atoms/GoTop";

// Containers
import HeaderContainer from "containers/organisms/HeaderContainer";
import SidebarContainer from "containers/organisms/SidebarContainer";
import CategoryMobileContainer from "containers/molecules/CategoryMobileContainer";
import TopNavigationMobileContainer from "containers/organisms/TopNavigationMobileContainer";
import CarouselBannersContainer from "containers/molecules/CarouselBannersContainer";

// Utils
import { getLocalStorageItem } from "utils/localStorage";

// Hooks
import { useNotificationPopups } from "hooks/useNotificationPopups";
import { useGTM } from "hooks/useGTM";
import { useLoaderStore } from "stores/loader";
import { useBreakpoint } from "hooks/useBreakpoint";
import * as userSelectors from "redux/user/selectors";
import { useSettingsStore } from "stores/settings";
import TabBarContainer from "../../../containers/molecules/TabBarContainer";
import BonusWidgetContainer from "../../../containers/molecules/BonusWidgetContainer";
import Footer from "../../organisms/Footer";

// Selectors
import * as authSelectors from "../../../redux/auth/selectors";

import "./styles.sass";

const CasinoLayout = () => {
  const location = useLocation();
  const GTM = useGTM();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const isSidebarOpen = useSettingsStore((state) => state.isSidebarOpen);
  const { isCustomBreakpointSmaller } = useBreakpoint(1370);
  const userId = useSelector(userSelectors.id);
  const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
  const isMainPageVisited = useRef(false);
  const { width, gridTemplateColumns } = useSpring({
    width: isCustomBreakpointSmaller ? 76 : isSidebarOpen ? 256 : 76,
    gridTemplateColumns: isCustomBreakpointSmaller
      ? "76px 1fr"
      : isSidebarOpen
        ? "256px 1fr"
        : "76px 1fr",
    transform: `rotate(${isSidebarOpen ? 0 : 180}deg)`,
  });
  const { overlayLoading } = useLoaderStore();

  useNotificationPopups();

  useEffect(() => {
    if (!token && !isMainPageVisited.current) {
      GTM.openMainPage(0);
      isMainPageVisited.current = true;
    }
    if (location.pathname === "/" && userId) {
      GTM.openMainPage(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, userId]);

  const ContainerClass = classNames("CasinoLayout__container", {
    loading: overlayLoading,
    auth: isAuthenticated,
  });

  const HeaderClass = classNames("CasinoLayout__header", {
    // eslint-disable-next-line camelcase
    CasinoLayout__header_auth: isAuthenticated,
  });
  const CategoryClass = classNames("CasinoLayout__category", {
    // eslint-disable-next-line camelcase
    CasinoLayout__category_auth: isAuthenticated,
  });

  return (
    <div className="CasinoLayout">
      <animated.div className={ContainerClass} style={{ gridTemplateColumns }}>
        <animated.div className={HeaderClass} style={{ gridTemplateColumns }}>
          <animated.div
            className="CasinoLayout__header_empty"
            style={{ width }}
          />
          <HeaderContainer />
        </animated.div>

        <div className={CategoryClass}>
          <TopNavigationMobileContainer />
          <div className="CasinoLayout__category_banners">
            <CarouselBannersContainer />
          </div>
          <div className="CasinoLayout__widget">
            <BonusWidgetContainer />
          </div>
          <CategoryMobileContainer />
        </div>

        <animated.div className="CasinoLayout__sidebar" style={{ width }}>
          <SidebarContainer />
        </animated.div>

        <div className="CasinoLayout__content">
          <div className="CasinoLayout__content_container">
            <Outlet />
          </div>
          <GoTop />
        </div>
        <TabBarContainer />
        <div className="CasinoLayout__footer">
          <Footer />
        </div>
      </animated.div>
    </div>
  );
};

export default CasinoLayout;
